
//#region import libraries
import React, { useState, useEffect, useMemo } from "react"
//bootstrap
import { Col, Row, Button } from "react-bootstrap";
//moment
import moment from "moment";
//tremor
import { Title as TremorTitle, Subtitle, DateRangePicker, Flex } from '@tremor/react';
//custom component
import { Card, CustomSpinner } from "_components/atoms";
import { ClickableTable } from "_components/organisms";
import { TableHeader, TableValue } from "_components/molecules";
import ActionsModal from "_components/molecules/Modals/actions-modal";
import Pagination from "./Pagination";
//redux
import { useDispatch, useSelector } from "react-redux"
import { selectUserDb } from "_redux/slices/main-screen/user";
import { getSurveyAnswers, getSurveyAnswersReport, selectQuestions, selectSelectedSurveyQuestionnair, selectSurveyAnswers, selectSurveyAnswersStatus, selectTotalRows, setSelectedSurveyQuestionnair, resetAnswersReportFlags, selectAnswersReport, selectAnswersReportStatus, getQuestions } from "_redux/slices/analytics/review-control-panel";
//react-select
import Select from "react-select";
//styles
import { Colors } from "_styles";
import { getDateOneWeekAgo, processName } from "_utils/functions";
//icons
import { FaRegFilePdf, FaStar } from "react-icons/fa";

import { getAirportBase64Logo, selectAirportBase64Logo } from "_redux/slices/analytics/reviews";
import { makeExcel } from "./makeExcel";
import { generateCaaSurveyReport } from "./cca-report";
import { sanitizeExcelWorkbook } from "_utils/sanitize-spreadsheet-cell";
//#endregion


const SurveysAnswers = () => {
    const CAA_AIRPORTS = ["BFS", "BRS", "EDI", "SOU", "ORK", "GLA"]; //Add list of valid iata to display button for generate pdf
    //#region redux
    const dispatch = useDispatch()
    const surveyQuestions = useSelector(selectQuestions);
    const userDb = useSelector(selectUserDb)
    const selectedSurveyQuestionnair = useSelector(selectSelectedSurveyQuestionnair);
    const surveysAnswers = useSelector(selectSurveyAnswers);
    const totalRows = useSelector(selectTotalRows)
    const surveysAnswersStatus = useSelector(selectSurveyAnswersStatus);
    const answersReport = useSelector(selectAnswersReport);
    const answersReportStatus = useSelector(selectAnswersReportStatus);
    const airportBase64Logo = useSelector(selectAirportBase64Logo)
    //#endregion

    //#region state
    const currentLanguage = userDb.details.authority === "PUJ" ? "es" : "en"
    const [surveysOptions, setSurveysOptions] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [questionAnswerPairs, setQuestionAnswerPairs] = useState({});
    const [date, setDate] = useState([getDateOneWeekAgo(new Date()), new Date()]);
    const [skip, setSkip] = useState(0);
    const [limit, setLimit] = useState(50);
    const [surveyLang, setSurveyLang] = useState(currentLanguage);
    const [loadingExcelFile, setLoadingExcelFile] = useState(false)
    const [loadingPDF, setLoadingPDF] = useState(false)
    //#endregion


    //#region useEffect

    useEffect(() => {
        if (userDb) {
            const data = { iata: userDb?.details.authority, passenger: false }
            dispatch(getQuestions(data))
        }
    }, [userDb])

    useEffect(() => {
        if (surveyQuestions.length) {
            let options = surveyQuestions.map(survey => {
                return { value: survey._id, label: survey.deleted ? `${survey.groupTitle} (deleted)` : survey.groupTitle }
            })
            options.unshift({ value: "ALL", label: "ALL" })
            setSurveysOptions(options)
        }

    }, [surveyQuestions])



    //need to update it with filters, send it to server
    useEffect(() => {

        if (date[0] && date[1] && selectedSurveyQuestionnair.value) {
            let data = {
                iata: userDb?.details.authority,
                surveyId: selectedSurveyQuestionnair.value,
                limit,
                skip,
                start: moment(new Date(date[0])).format().split("T")[0],
                end: moment(new Date(date[1])).format().split("T")[0],
                report: false
            }
            dispatch(getSurveyAnswers(data))
        }

    }, [selectedSurveyQuestionnair, date, skip, limit]) // for later add pagination 

    useEffect(() => {
        if (answersReportStatus === "succeeded" && airportBase64Logo) {
            handleNewExcel(answersReport)
        }
    }, [answersReportStatus, airportBase64Logo])

    //#endregion 

    const processDate = (value) => {
        return moment(value).format("YYYY-MM-DD:hh:mm A")
    }


    const columns = useMemo(
        () => [

            {
                Header: <TableHeader title={'Survey Title'} />,
                accessor: "surveyTitle",
                Cell: ({ cell: { value } }) => <p>{value}</p>
            },
            {
                Header: <TableHeader title={'Name'} />,
                accessor: "name",
                Cell: ({ cell: { value } }) => <TableValue title={processName(value)} />
            },
            {
                Header: <TableHeader title={'Completed At'} />,
                accessor: "date",
                Cell: ({ cell: { value } }) => <TableValue title={processDate(value)} />
            },
            {
                Header: <TableHeader title={'Flight'} />,
                accessor: "flightNumber",
                Cell: ({ cell: { value } }) => <TableValue title={value} />
            },
            {
                Header: <TableHeader title={'EmployeeID'} />,
                accessor: "EmployeeId",
                Cell: ({ cell: { value } }) => <TableValue title={value} />
            }

        ],
        []
    );


    //#region functions
    const changeSurveyType = (selection) => {
        dispatch(setSelectedSurveyQuestionnair(selection))
    }

    const handleExcel = async (reportData) => {
        const reportColumns = ["Survey title", "Questions & Answers", "", "Passenger name", "Booking ID", "Origin", "Destination", "Flight number", "Task ID", "Employee ID", "Created date"]
        const exportedFileName = `survey answers_${userDb?.details.authority}_${moment(new Date(date[0])).format().split("T")[0]}_${moment(new Date(date[1])).format().split("T")[0]}`
        let data = {};

        data.fileName = exportedFileName;
        data.workSheetName = [exportedFileName];
        data.columnTitles = [reportColumns];
        data.columnWidth = [reportColumns?.map((row) => row.length + 10)];

        data.rows = [reportData]
        await makeExcel(data, userDb?.details.authority, airportBase64Logo);
        setLoadingExcelFile(false)
        dispatch(resetAnswersReportFlags())
    }
    const handleNewExcel = async (reportData) => {
        try {
            const Excel = await import("exceljs/dist/exceljs.min.js");
            const exportedFileName = `survey_answers_${userDb?.details.authority}_${moment(new Date(date[0])).format().split("T")[0]}_${moment(new Date(date[1])).format().split("T")[0]}`;

            // Get the selected survey to extract questions
            const selectedSurvey = surveyQuestions.find(s => s._id === selectedSurveyQuestionnair.value);

            // Build question list from selected survey or from all answers
            let questionList = [];
            let questionIdToIndex = {};

            if (selectedSurvey && selectedSurvey.questions) {
                // Use questions from the selected survey
                selectedSurvey.questions.forEach((q, index) => {
                    const questionText = q.text?.en || q.text?.es || Object.values(q.text || {})[0] || `Question ${index + 1}`;
                    questionList.push({ index: index + 1, questionId: q.questionId, text: questionText, type: q.type });
                    questionIdToIndex[q.questionId] = index + 1;
                });
            } else {
                // Extract unique questions from all report data
                const uniqueQuestions = new Map();
                reportData.forEach(item => {
                    if (item.answers) {
                        Object.keys(item.answers).forEach(questionId => {
                            if (!uniqueQuestions.has(questionId)) {
                                uniqueQuestions.set(questionId, questionId);
                            }
                        });
                    }
                });
                let idx = 1;
                uniqueQuestions.forEach((text, questionId) => {
                    // no survey picked: take the type from whichever loaded survey owns this question
                    const type = (surveyQuestions || [])
                        .flatMap((survey) => survey.questions || [])
                        .find((question) => question.questionId === questionId)?.type;
                    questionList.push({ index: idx, questionId, text, type });
                    questionIdToIndex[questionId] = idx;
                    idx++;
                });
            }

            // Create workbook
            let workbook = new Excel.Workbook();
            let worksheet = workbook.addWorksheet(exportedFileName);

            // Add "Question List:" header
            worksheet.addRow(["Question List:"]);
            worksheet.getRow(1).font = { bold: true, size: 14 };
            worksheet.getRow(1).height = 25;

            // Add questions with serial numbers
            questionList.forEach(q => {
                const row = worksheet.addRow([q.index, q.text]);
                row.getCell(1).font = { bold: true };
                row.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
                row.getCell(2).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
            });

            // Add empty rows for spacing
            worksheet.addRow([]);
            worksheet.addRow([]);

            // Build column headers for data section: Date, Booking ID, 1, 2, 3, ...
            const dataHeaders = ["Date", "Booking ID"];
            questionList.forEach(q => {
                dataHeaders.push(q.index.toString());
            });

            // Add data header row
            const headerRow = worksheet.addRow(dataHeaders);
            headerRow.font = { bold: true, color: { argb: '3b7ab4' } };
            headerRow.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'e1faf5' }
            };
            headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
            headerRow.height = 25;

            // Add borders to header
            headerRow.eachCell((cell) => {
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });

            // Helper function to format answer value
            const formatAnswer = (answer, question) => {
                if (answer === null || answer === undefined) return '';
                // scales: portal form saves [1..n] (score n), the passenger app saves the number
                if (question?.type === 'scale5' || question?.type === 'scale10') {
                    return Array.isArray(answer) ? answer.length : answer;
                }
                if (typeof answer === 'string') return answer;
                if (typeof answer === 'number') return answer.toString();
                if (Array.isArray(answer)) return answer.join(', ');
                if (typeof answer === 'object') {
                    // Handle checkbox/hybrid with options
                    if (answer.options && Array.isArray(answer.options)) {
                        let result = answer.options.join(', ');
                        if (answer.comment) result += ` (Comment: ${answer.comment})`;
                        return result;
                    }
                    // Handle matrix answers
                    if (Object.keys(answer).every(key => !isNaN(parseInt(key)))) {
                        return Object.entries(answer).map(([k, v]) => `${parseInt(k) + 1}: ${v}`).join('; ');
                    }
                    return JSON.stringify(answer);
                }
                return String(answer);
            };

            // Add data rows
            reportData.forEach(item => {
                const rowData = [
                    item.createdAt ? moment(item.createdAt).format('YYYY-MM-DD') : (item.date || ''),
                    item.bookingId || item.reviewId || ''
                ];

                // Add answers in order of question index
                questionList.forEach(q => {
                    const answer = item.answers ? item.answers[q.questionId] : '';
                    rowData.push(formatAnswer(answer, q));
                });

                const dataRow = worksheet.addRow(rowData);
                dataRow.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

                // Add borders to data cells
                dataRow.eachCell((cell) => {
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                });
            });

            // Set column widths
            worksheet.getColumn(1).width = 12; // Date
            worksheet.getColumn(2).width = 30; // Booking ID
            // Set width for question columns
            for (let i = 3; i <= questionList.length + 2; i++) {
                worksheet.getColumn(i).width = 15;
            }

            // Also set width for question text column in the question list section
            worksheet.getColumn(2).width = Math.max(30, 80);

            // Write and download file
            sanitizeExcelWorkbook(workbook);
            const buf = await workbook.xlsx.writeBuffer();
            const downloadUrl = window.URL.createObjectURL(new Blob([buf]));
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.setAttribute("download", `${exportedFileName}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (error) {
            console.error("Error generating Excel:", error);
        }

        setLoadingExcelFile(false);
        dispatch(resetAnswersReportFlags());
    }

    //skip next rows (limit 10,25,50...)
    const showMore = () => {
        setSkip(skip + limit)
    }
    //skip to previous rows (limit 10,25,50...)
    const showLess = () => {
        setSkip(skip - limit)
    }
    //chnage number of rows per page
    const handleLimitChange = (event) => {
        const newLimit = Number(event.target.value);
        setLimit(newLimit);
    }

    const handleAnswer = (answer, question, language) => {

        // scale answers come as an array from the portal form ([1,2,3] = 3) or a plain number from the passenger app
        if (question?.type === "scale5" || question?.type === "scale10") {
            const score = Array.isArray(answer) ? answer.length : Number(answer);
            const max = question.type === "scale5" ? 5 : 10;
            return Number.isFinite(score) ? `${score} / ${max}` : "";
        }

        // Handle scale arrays (e.g., [1,2,3,4,5])
        if (Array.isArray(answer)) return answer.length;

        if (typeof answer === "number") return String(answer);

        // Handle objects
        if (typeof answer === "object") {
            // Check if it's a matrix-scale5 answer (has numeric keys like "0", "1", "2")
            const isMatrixAnswer = question?.type === 'matrix-scale5' &&
                Object.keys(answer).every(key => !isNaN(parseInt(key)));

            if (isMatrixAnswer && question?.items) {
                // Format matrix answers with sub-question text and scale labels
                return (
                    <div style={{ marginLeft: 10 }}>
                        {Object.entries(answer).map(([rowIndex, scaleValue]) => {
                            const item = question.items[parseInt(rowIndex)];
                            const itemText = item?.text?.[language] || item?.text?.en || `Item ${parseInt(rowIndex) + 1}`;

                            // Get scale label if available
                            const scaleLabel = question.scaleLabels?.[language]?.[scaleValue - 1] ||
                                question.scaleLabels?.en?.[scaleValue - 1] ||
                                scaleValue;

                            return (
                                <div key={rowIndex} style={{ marginBottom: 8, padding: '4px 0', borderBottom: '1px solid #eee' }}>
                                    <div style={{ fontWeight: 500, fontSize: 14 }}>{itemText}</div>
                                    <div style={{ color: '#666', fontSize: 13 }}>
                                        {scaleLabel}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            }

            // Handle checkbox/hybrid answers with options and comment
            return (
                <>
                    {Array.isArray(answer.options) &&
                        answer.options.map((option, index) => (
                            <div key={index}>
                                {index + 1}-{option}
                            </div>
                        ))}
                    {answer.comment && (
                        <div>
                            <strong>comment:</strong> {answer.comment}
                        </div>
                    )}
                </>
            );
        }
    }

    // Helper to render stars for rating type
    const renderStars = (value, totalStars = 5) => {
        const selectedValue = parseInt(value) || 0;
        return (
            <div className="d-flex align-items-center gap-1">
                {Array.from({ length: totalStars }, (_, i) => (
                    <FaStar
                        key={i + 1}
                        size={18}
                        color={selectedValue >= i + 1 ? "#FFD700" : "#ccc"}
                    />
                ))}
                <span style={{ marginLeft: 8, color: '#666', fontSize: 14 }}>
                    ({selectedValue}/{totalStars})
                </span>
            </div>
        );
    };

    //click a row to display questions with its answers
    const handleClick = (props) => {
        //props is an index of row in the table
        const survey = surveysAnswers[props];

        const matchingSurvey = surveyQuestions.find(surveyQuestion => surveyQuestion._id === survey.surveyId);


        //getting questions text ,by compare each id of answer with question id
        const dataForModal = Object.fromEntries(
            Object.entries(survey.answers).map(([questionId, answer]) => {
                // Find the question with the matching questionId
                const question = matchingSurvey?.questions?.find(q => q.questionId === questionId);

                // Extract question text in the specified language, fallback to `questionId` if not found
                const questionText = question && question.text[survey.language] ? question.text[survey.language] : questionId;

                // Format the answer based on question type
                let formattedAnswer;
                if (question?.type === "rating") {
                    // Display stars for rating type
                    const totalStars = question.starsCount || question.options?.[survey.language]?.length || 5;
                    formattedAnswer = renderStars(answer, totalStars);
                } else if (typeof answer === "string") {
                    formattedAnswer = answer;
                } else {
                    formattedAnswer = handleAnswer(answer, question, survey.language);
                }

                return [questionText, formattedAnswer];
            })
        )
        setQuestionAnswerPairs(dataForModal)
        setOpenModal(true)
        setSurveyLang(survey.language)
    }

    const handleCloseModal = () => {
        setOpenModal(false); // close modal;
        setQuestionAnswerPairs({})
        setSurveyLang(currentLanguage)

    };

    const downloadSurveyAnswersReport = () => {

        const data = {
            skip,
            limit: 1000,
            report: true,
            start: moment(new Date(date[0])).format().split("T")[0],
            end: moment(new Date(date[1])).format().split("T")[0],
            iata: userDb?.details.authority,
            surveyId: selectedSurveyQuestionnair.value,
        }
        dispatch(getSurveyAnswersReport(data));
        if (!airportBase64Logo) dispatch(getAirportBase64Logo(userDb?.details.authority))
        setLoadingExcelFile(true);
    };

    const renderSurveyReport = () => {
        setLoadingPDF(true)
        const authority = userDb?.details.authority;
        const surveyId = ["BFS"].includes(authority) ? `${authority}_caa-survey` : `${authority}_caa-passenger-survey`
        const survey = surveyQuestions.find(surveyQ => surveyQ._id === surveyId);

        if (!survey) {
            alert("CAA Survey not found for this airport");
            return;
        }

        if (!surveysAnswers || surveysAnswers.length === 0) {
            alert("No survey responses available. Please select a date range with responses.");
            return;
        }

        // Format date range for the report
        const dateRange = date[0] && date[1]
            ? `${moment(new Date(date[0])).format("DD/MM/YYYY")} - ${moment(new Date(date[1])).format("DD/MM/YYYY")}`
            : "";

        const success = generateCaaSurveyReport(survey, surveysAnswers, {
            iata: authority,
            dateRange
        });

        if (!success) {
            alert("Failed to generate report. Please check the survey data.");
        } else { setLoadingPDF(false) }
    }
    //#endregion

    //#region rendering

    return (<>
        {/*TITLE/ FILTERS(DATE PICKER/SURVEY)*/}
        <Row className="mt-4">
            <Col md={5}>
                <div >
                    <TremorTitle>Surveys answers</TremorTitle>
                    <Subtitle>{'View details of surveys answers from passengers'}</Subtitle>
                </div>
            </Col>
            <Col md={4}>
                <DateRangePicker
                    onValueChange={(value) => setDate(value)}
                    value={date}
                    enableDropdown={true}
                    maxWidth="max-w-lg"
                    dropdownPlaceholder={"Last 7 Days"}
                />
            </Col>
            <Col md={3}>
                <Select
                    defaultValue={"ALL"}
                    options={surveysOptions}
                    placeholder="Displayed Surveys..."
                    onChange={(selection) => changeSurveyType(selection)}
                    value={selectedSurveyQuestionnair}
                />
            </Col>

        </Row>
        {/*TABLE + PAGINATION COMPONENTS*/}
        <Card>
            <div style={{ overflowY: 'scroll', height: '40em', }}>
                {
                    <ClickableTable
                        columns={columns}
                        data={surveysAnswers.length ? surveysAnswers : []}
                        handleClick={handleClick}
                        status={surveysAnswersStatus}
                        clickable={true}
                    />
                }
            </div>


            <Flex>

                <Button
                    variant={"outline-primary"}
                    onClick={downloadSurveyAnswersReport}
                    loading={true}
                    style={{ width: 150, marginLeft: 15 }}
                >

                    {answersReportStatus == "loading" || loadingExcelFile ? <CustomSpinner /> : "Export"}
                </Button>

                {CAA_AIRPORTS.includes(userDb?.details.authority) && <Button
                    variant={"outline-primary"}
                    onClick={renderSurveyReport}
                    loading={true}
                    style={{ width: 250 }}
                >

                    {loadingPDF ? <CustomSpinner /> : "Generate Report"}
                    <FaRegFilePdf style={{ marginLeft: 5 }} />
                </Button>}

                {/*New simple pagination*/}
                <Row className="mt-2">

                    <div style={{ float: "inline-end", marginRight: "40px" }}>
                        <Pagination
                            showLess={showLess}
                            showMore={showMore}
                            limit={limit}
                            skippedRows={skip}
                            totalRows={totalRows}
                            handleLimitChange={handleLimitChange}

                        />
                    </div>

                </Row>
            </Flex>

        </Card>

        {/*modal to show question/answers pairs*/}
        <ActionsModal
            show={openModal}
            handleClose={handleCloseModal}
            title={surveyLang === "ar" ? "إجابات الاستبيان" : "Survey answers"}
            minWidth="35%"
            maxWidth="35%"
            rtl={true}
            isTimeline={false}
        >
            <div style={{
                direction: surveyLang === "ar" ? "rtl" : "ltr",
                textAlign: surveyLang === "ar" ? "right" : "left"
            }}>
                {Object.entries(questionAnswerPairs).map(([question, answer], index) => (
                    <div key={index} style={{ marginBottom: '1rem' }}>
                        <p style={{ fontSize: 16, color: Colors.PRM_LIGHT_BLUE }}>{question}</p>
                        <p >{answer}</p>
                    </div>
                ))}
            </div>
        </ActionsModal>
    </>
    )

    //#endregion
}

export default SurveysAnswers;
